(function () {
  'use strict';

  const config = window.DRNGravityLocations || {};
  const formConfigs = Array.isArray(config.forms) ? config.forms : [];
  const MANUAL_VALUE = config.manualValue || '__drn_manual__';
  const MANUAL_LABEL = config.manualLabel || 'Not listed — enter manually';

  function fieldSelector(addressFieldId, part) {
    return `[name="input_${addressFieldId}.${part}"]`;
  }

  function getForm(formId) {
    return document.getElementById(`gform_${formId}`);
  }

  function findPart(form, addressFieldId, part) {
    if (!form) return null;
    return form.querySelector(fieldSelector(addressFieldId, part));
  }

  function copyElementAttributes(from, to) {
    Array.from(from.attributes || []).forEach((attr) => {
      if (attr.name === 'type' || attr.name === 'value') return;
      to.setAttribute(attr.name, attr.value);
    });
  }

  function replaceWithSelect(element, options, placeholder, selectedValue) {
    if (!element) return null;

    const select = document.createElement('select');
    copyElementAttributes(element, select);

    // Keep Gravity Forms styling when converting a text input to select.
    select.classList.add('gfield_select');
    select.removeAttribute('type');

    select.appendChild(new Option(placeholder, ''));

    (options || []).forEach((value) => {
      select.appendChild(new Option(value, value));
    });

    // Always allow a user to enter a valid location missing from the dataset.
    select.appendChild(new Option(MANUAL_LABEL, MANUAL_VALUE));

    if (selectedValue && options.includes(selectedValue)) {
      select.value = selectedValue;
    }

    element.replaceWith(select);
    return select;
  }

  function replaceWithTextInput(element, placeholder, selectedValue) {
    if (!element || element.tagName === 'INPUT') {
      if (element && placeholder) element.placeholder = placeholder;
      return element;
    }

    const input = document.createElement('input');
    copyElementAttributes(element, input);
    input.type = 'text';
    input.classList.remove('gfield_select');
    if (placeholder) input.placeholder = placeholder;
    if (selectedValue) input.value = selectedValue;

    element.replaceWith(input);
    return input;
  }

  function switchToManualInput(element, placeholder) {
    if (!element) return null;

    const input = replaceWithTextInput(element, placeholder, '');
    if (!input) return null;

    input.value = '';
    input.dataset.drnManual = '1';

    window.setTimeout(() => {
      try {
        input.focus();
      } catch (e) {}
    }, 0);

    return input;
  }

  function setSubLabel(form, addressFieldId, part, text) {
    const id = `input_${form.id.replace('gform_', '')}_${addressFieldId}_${part.replace('.', '_')}`;
    const label = form.querySelector(`label[for="${id}"]`);
    if (label) label.textContent = text;
  }

  async function requestOptions(requestType, country, state = '') {
    const body = new URLSearchParams();
    body.set('action', 'drn_location_options');
    body.set('nonce', config.nonce || '');
    body.set('request_type', requestType);
    body.set('country', country || '');
    if (state) body.set('state', state);

    const response = await fetch(config.ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
      body: body.toString()
    });

    if (!response.ok) return [];

    const json = await response.json();
    if (!json || !json.success || !json.data) return [];

    return Array.isArray(json.data.options) ? json.data.options : [];
  }

  async function loadRegions(formId, addressFieldId, preserveCurrent = true) {
    const form = getForm(formId);
    if (!form) return;

    const countryEl = findPart(form, addressFieldId, '6');
    let stateEl = findPart(form, addressFieldId, '4');
    let cityEl = findPart(form, addressFieldId, '3');

    if (!countryEl || !stateEl || !cityEl) return;

    const country = countryEl.value.trim();
    const currentState = preserveCurrent ? stateEl.value.trim() : '';

    if (!country) {
      replaceWithTextInput(stateEl, 'State / Province / Region', currentState);
      replaceWithTextInput(cityEl, 'City', preserveCurrent ? cityEl.value.trim() : '');
      return;
    }

    stateEl.setAttribute('aria-busy', 'true');
    cityEl.setAttribute('aria-busy', 'true');

    const regions = await requestOptions('regions', country);

    // If our dataset has no regions for this country, let Gravity Forms remain
    // flexible by using normal text fields instead of blocking the address.
    if (!regions.length) {
      stateEl = replaceWithTextInput(stateEl, 'State / Province / Region', currentState);
      cityEl = replaceWithTextInput(cityEl, 'City', preserveCurrent ? cityEl.value.trim() : '');
      if (stateEl) stateEl.removeAttribute('aria-busy');
      if (cityEl) cityEl.removeAttribute('aria-busy');
      return;
    }

    // If an existing region value is not in the lookup, it was likely
    // entered manually. Preserve it as a text field instead of blanking it.
    if (currentState && !regions.includes(currentState)) {
      stateEl = replaceWithTextInput(
        stateEl,
        'State / Province / Region',
        currentState
      );

      cityEl = replaceWithTextInput(
        cityEl,
        'City',
        preserveCurrent ? cityEl.value.trim() : ''
      );

      if (stateEl) {
        stateEl.dataset.drnManual = '1';
        stateEl.removeAttribute('aria-busy');
      }

      if (cityEl) {
        cityEl.dataset.drnManual = '1';
        cityEl.removeAttribute('aria-busy');
      }

      return;
    }

    stateEl = replaceWithSelect(
      stateEl,
      regions,
      'Select State / Province / Region',
      currentState
    );

    if (stateEl) stateEl.removeAttribute('aria-busy');

    // Existing city can only be safely retained when the existing state also survived.
    const stateWasRetained = !!(currentState && stateEl && stateEl.value === currentState);
    const currentCity = preserveCurrent && stateWasRetained ? cityEl.value.trim() : '';

    cityEl = replaceWithSelect(cityEl, [], 'Select City', '');
    if (cityEl) cityEl.removeAttribute('aria-busy');

    if (stateWasRetained) {
      await loadCities(formId, addressFieldId, currentCity);
    }
  }

  async function loadCities(formId, addressFieldId, selectedCity = '') {
    const form = getForm(formId);
    if (!form) return;

    const countryEl = findPart(form, addressFieldId, '6');
    const stateEl = findPart(form, addressFieldId, '4');
    let cityEl = findPart(form, addressFieldId, '3');

    if (!countryEl || !stateEl || !cityEl) return;

    const country = countryEl.value.trim();
    const state = stateEl.value.trim();

    if (!country || !state) {
      cityEl = replaceWithSelect(cityEl, [], 'Select City', '');
      if (cityEl) cityEl.removeAttribute('aria-busy');
      return;
    }

    cityEl.setAttribute('aria-busy', 'true');

    const cities = await requestOptions('cities', country, state);

    if (!cities.length) {
      cityEl = replaceWithTextInput(cityEl, 'City', selectedCity);

      if (cityEl) {
        if (selectedCity) cityEl.dataset.drnManual = '1';
        cityEl.removeAttribute('aria-busy');
      }

      return;
    }

    // Preserve an existing manually entered city if the lookup has cities
    // but does not contain that specific value.
    if (selectedCity && !cities.includes(selectedCity)) {
      cityEl = replaceWithTextInput(cityEl, 'City', selectedCity);

      if (cityEl) {
        cityEl.dataset.drnManual = '1';
        cityEl.removeAttribute('aria-busy');
      }

      return;
    }

    cityEl = replaceWithSelect(cityEl, cities, 'Select City', selectedCity);
    if (cityEl) cityEl.removeAttribute('aria-busy');
  }

  function bindForm(formId, addressFieldId) {
    const form = getForm(formId);
    if (!form || form.dataset.drnLocationsBound === '1') return;

    form.dataset.drnLocationsBound = '1';

    setSubLabel(form, addressFieldId, '4', 'State / Province / Region');

    form.addEventListener('change', async function (event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (
        target.matches(fieldSelector(addressFieldId, '4')) &&
        target.value === MANUAL_VALUE
      ) {
        const manualState = switchToManualInput(
          target,
          'State / Province / Region'
        );

        let cityEl = findPart(form, addressFieldId, '3');
        cityEl = switchToManualInput(cityEl, 'City');
        return;
      }

      if (
        target.matches(fieldSelector(addressFieldId, '3')) &&
        target.value === MANUAL_VALUE
      ) {
        switchToManualInput(target, 'City');
        return;
      }

      if (target.matches(fieldSelector(addressFieldId, '6'))) {
        // Gravity Forms may alter the state field after country changes.
        // Give it a tick, then impose the DRN region/city selectors.
        window.setTimeout(function () {
          loadRegions(formId, addressFieldId, false);
        }, 50);
        return;
      }

      if (target.matches(fieldSelector(addressFieldId, '4'))) {
        await loadCities(formId, addressFieldId, '');
      }
    });

    // Initial render, including edit forms with pre-populated values.
    loadRegions(formId, addressFieldId, true);
  }

  function initAll() {
    formConfigs.forEach((item) => {
      bindForm(Number(item.formId), Number(item.addressFieldId));
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);

  // Gravity Forms AJAX render event (current versions).
  document.addEventListener('gform/post_render', initAll);

  // Gravity Forms legacy jQuery render event.
  if (window.jQuery) {
    window.jQuery(document).on('gform_post_render', initAll);
  }
})();
